/**
 * The multi-track log display.
 *
 * Curves are drawn as SVG paths in a fixed 0–100 × 0–1000 viewBox with
 * `preserveAspectRatio="none"`, exactly as the mockup does: the track stretches
 * to whatever width the layout gives it while the depth axis stays linear, and
 * `vector-effect="non-scaling-stroke"` keeps the line weight honest.
 *
 * Shared by the workstation (1a) and the A3 print sheet (1f).
 */

import { useMemo, useRef, useState, type CSSProperties } from "react";
import { useElementSize, useRampsVersion, useReorderDrag } from "../hooks";
import { ColorBar } from "./ColorBar";
import { CurveQuickEdit, SpectrumQuickEdit } from "./LogQuickEdit";
import { makePainter, rampColor, rampStops } from "../lib/colorscale";
import { axisFraction, axisSpan } from "../lib/depthaxis";
import {
  axisTicks,
  dashArrayFor,
  formatReading,
  formatScaleValue,
  resolveAxisRange,
  thinScaleLabels,
  type AxisScale,
  type CurveConfig,
  type CurveFill,
  type CurveStyle,
  type CrossFill,
  type SpectrumConfig,
} from "../lib/tracklayout";
import type { CurveData } from "../types";

export interface TrackCurve {
  name: string;
  /**
   * Siapa kurva ini pada susunan yang memilikinya — bukan apa namanya.
   *
   * Nama sebuah kurva adalah tulisan di legendanya, dan tulisan itu tidak
   * dijamin unik: satu track boleh memuat dua kurva bernama sama, dan pada
   * panel korelasi bertumpuk nama kurva justru nama sumurnya. Yang menyunting
   * harus tahu kurva mana yang diklik, dan nama tidak bisa menjawab itu —
   * suntingan yang dialamatkan lewat nama selalu jatuh pada yang pertama.
   *
   * Isinya milik yang membangun lembar: lembar log memakai kedudukan kurva di
   * dalam susunan tracknya, lembar korelasi memakai id sumur. Kosong berarti
   * kembali memakai nama, yang tetap benar selama namanya memang unik.
   */
  key?: string;
  color: string;
  /** Display scale. min > max flips the axis — NPHI and SW are drawn reversed. */
  min: number;
  max: number;
  unit?: string;
  rangeLabel?: string;
  /** Bentuk garis. `dashed` yang lama tetap dibaca sebagai `"dashed"`. */
  style?: CurveStyle;
  dashed?: boolean;
  /** Ketebalan garis kurva ini sendiri, menggantikan ketebalan lembar. */
  width?: number;
  /** Benar bila kedua ujung skalanya disetel sendiri alih-alih diwarisi dari
   *  skala tampilan kurva. Hanya dibaca oleh kotak sunting di kepala track,
   *  yang perlu tahu apakah tombol Auto masih ada gunanya. */
  customScale?: boolean;
  logarithmic?: boolean;
  /**
   * Arsiran ke salah satu ujung skala kurva ini.
   *
   * Warna yang kosong diterjemahkan menjadi warna kurvanya sendiri di tempat
   * arsiran itu digambar, bukan di tempat lembar disusun: kosong berarti
   * "ikut kurva", dan itu keadaan yang harus tetap bisa dibedakan dari warna
   * yang kebetulan sama — kotak setelan di kepala track memperlihatkannya
   * sebagai tombol yang sedang menyala.
   */
  fill?: CurveFill;
  depths: number[];
  values: (number | null)[];
}

/** Bentuk garis yang berlaku, setelah medan `dashed` lama diperhitungkan. */
export function curveStyleOf(curve: TrackCurve): CurveStyle {
  return curve.style ?? (curve.dashed ? "dashed" : "solid");
}

export interface TrackSpec {
  /** Tetap sepanjang umur track — dua track boleh berjudul sama. */
  id: string;
  title: string;
  units: string;
  flex: number;
  curves: TrackCurve[];
  lithology?: boolean;
  described?: boolean;
  classes?: boolean;
  /**
   * Kolom pita warna yang **bukan** kelas LRLC — bendera reservoir, bendera
   * pay, selang uji produksi.
   *
   * Digambar oleh penggambar yang sama dengan kolom kelas, sebab bentuknya
   * memang sama: selang yang dicat, bukan garis. Yang berbeda hanya kalimat
   * ketika ia kosong, dan kalimat itu datang lewat `emptyNote` — sebuah kolom
   * uji produksi yang berbunyi "No LRLC curve on this well" mengirim
   * pembacanya mencari kurva yang tidak ada hubungannya.
   */
  bands?: string;
  logarithmic?: boolean;
  shading?: CrossFill;
  /** Pita warna, bila track ini digambar sebagai warna alih-alih garis. */
  spectrum?: TrackSpectrum;
  /** Garis tegak pada sebuah nilai — ambang, batas, garis air. */
  references?: TrackReference[];
}

/**
 * Sebuah garis tegak pada nilai tertentu di dalam track.
 *
 * Nilainya diproyeksikan dengan skala **kurva yang disebutnya**, bukan dengan
 * skala track: sebuah track boleh memuat dua kurva berskala berbeda, dan garis
 * ambang yang diletakkan menurut skala yang salah berdiri di tempat yang
 * salah — persis seperti sebuah cut-off yang terbaca benar tetapi memotong di
 * angka lain. Kurva yang tidak tergambar berarti garisnya tidak digambar:
 * ambang tanpa sesuatu untuk dibandingkan bukan ambang.
 */
export interface TrackReference {
  /** Nama kurva yang skalanya dipakai, dan yang dibandingkan dengan garis ini. */
  curve: string;
  value: number;
  color: string;
  /** Tulisan kecil di kaki garisnya; kosong berarti tanpa tulisan. */
  label?: string;
}

/**
 * Pita warna sebagaimana benar-benar digambar.
 *
 * Berbeda dengan `SpectrumConfig` yang tersimpan, di sini kedua ujung
 * jepitannya sudah pasti berupa angka: yang menggambar tidak boleh perlu tahu
 * dari mana angka itu datang — setelan pengguna atau skala tampilan kurvanya —
 * dan yang menyunting tetap bisa membedakannya dari config aslinya.
 */
export interface TrackSpectrum {
  /** Nama kurva yang diwarnai. */
  curve: string;
  ramp: string;
  reverse: boolean;
  lo: number;
  hi: number;
  /** Lebar batang ikut nilai juga, bukan pita selebar penuh. */
  bars: boolean;
  /** Benar bila jepitannya disetel sendiri, bukan diwarisi dari skala kurva. */
  custom: boolean;
}

export interface ZoneBand {
  name: string;
  top: number;
  base: number;
  color: string;
}

export interface MarkerLine {
  name: string;
  md: number;
  color: string;
}

/**
 * Lebar kotak gambar sebuah track dalam satuan viewBox.
 *
 * Diekspor bersama `unproject`: yang memetakan balik sebuah kedudukan mendatar
 * menjadi nilai harus memakai satuan yang sama dengan yang memetakannya ke
 * sana, dan sebuah pemanggil yang menebak angkanya sendiri akan meleset diam-
 * diam pada setiap sumbu logaritmik.
 */
export const VIEW_W = 100;

/** Opasitas setiap arsiran — kurva di bawahnya harus tetap terbaca. */
const FILL_OPACITY = 0.28;

/**
 * Opasitas arsiran yang warnanya mengikuti bacaan.
 *
 * Lebih pekat, dan bukan karena lebih penting. Arsiran biasa hanya menyatakan
 * "di sini ada", dan sapuan setipis apa pun sudah menyatakannya; arsiran yang
 * warnanya *adalah* nilainya harus bisa dibaca sebagai warna — dua bacaan yang
 * berbeda seperempat skala tidak boleh menjadi dua warna yang sama pucatnya.
 * Kurvanya sendiri digambar sesudah arsiran, jadi ia tetap di atas.
 */
const VALUE_FILL_OPACITY = 0.55;

/**
 * Pengenal gradien arsiran, unik dalam satu dokumen.
 *
 * `url(#…)` dicari di seluruh halaman, bukan di dalam SVG yang memakainya, jadi
 * dua track yang sama-sama mengarsir VSH akan saling merebut gradien kalau
 * pengenalnya hanya nama kurva. Nomor urut komponen yang membedakannya.
 *
 * Di dalam satu track, yang membedakan adalah kedudukan kurva dan bukan
 * namanya. Nama sebuah kurva tidak dijamin unik: susunan track disimpan di
 * peramban dan dibaca kembali apa adanya, jadi susunan yang menyebut satu
 * mnemonic dua kali menghasilkan dua kurva bernama sama — dan dua gradien
 * berpengenal sama membuat peramban memakai yang pertama untuk keduanya, jadi
 * kurva kedua terarsir dengan bacaan kurva pertama. Warna yang salah tidak
 * memunculkan error apa pun; ia hanya sebuah lembar yang berbohong.
 */
let gradientSeq = 0;

/**
 * Lebar terkecil sebuah track selebar satu satuan flex, dalam piksel.
 *
 * Tanpa lantai ini track hanya berbagi lebar panel: menambah track kesembilan
 * memampatkan delapan lainnya sampai tidak ada satu pun yang terbaca, dan
 * melebarkan satu track selalu berarti menyempitkan yang lain. Dengan lantai
 * ini lembar boleh tumbuh melampaui panelnya, lalu digeser mendatar.
 */
export const TRACK_MIN_PX = 132;

/** Lebar mistar kedalaman. Kepala track dan lembarnya harus sepakat. */
export const DEPTH_GUTTER_W = 62;

/**
 * Jarak kisi mendatar bawaan, piksel.
 *
 * Berdiri sebagai nama sebab dua pihak harus menyebut angka yang sama: track
 * yang menggambar kisinya, dan lembar yang membulatkan tingginya ke kelipatan
 * angka itu supaya tidak ada petak buntung di kakinya.
 */
export const DEPTH_GRID_PX = 24;

/**
 * Lebar terkecil sederet track, dalam piksel.
 *
 * Dipakai kepala track dan lembar di bawahnya untuk mengumumkan lebar yang
 * sama kepada pembungkusnya masing-masing. Keduanya harus melebar bersama:
 * kepala yang lebih sempit satu piksel dari tracknya adalah kepala yang
 * berbohong tentang skala, dan itu terbaca sebagai kesalahan data.
 */
export function tracksMinWidth(tracks: TrackSpec[]): number {
  return tracks.reduce((sum, track) => sum + track.flex * TRACK_MIN_PX, 0);
}

const VIEW_H = 1000;

/** Map a value onto 0–100 across its display scale, honouring reversed axes. */
/**
 * Kebalikan `project`: nilai apa yang berada di kedudukan mendatar `x`.
 *
 * Dipakai arsiran bergradasi. Perhentian gradien SVG diletakkan pada pecahan
 * lebar, sedangkan warnanya harus mengikuti nilai — dan pada sumbu logaritmik
 * keduanya tidak sebanding. Menghitung nilainya kembali membuat satu ramp yang
 * sama benar pada sumbu linear maupun logaritmik, tanpa cabang di pemanggilnya.
 */
export function unproject(x: number, min: number, max: number, logarithmic?: boolean): number {
  const t = x / VIEW_W;
  if (logarithmic) {
    if (min <= 0 || max <= 0) return NaN;
    const lo = Math.log10(min);
    return 10 ** (lo + t * (Math.log10(max) - lo));
  }
  return min + t * (max - min);
}

export function project(value: number, min: number, max: number, logarithmic?: boolean): number {
  let v = value;
  let lo = min;
  let hi = max;
  if (logarithmic) {
    if (v <= 0 || lo <= 0 || hi <= 0) return NaN;
    v = Math.log10(v);
    lo = Math.log10(lo);
    hi = Math.log10(hi);
  }
  const span = hi - lo;
  if (Math.abs(span) < 1e-12) return NaN;
  return ((v - lo) / span) * VIEW_W;
}

/** Pecahan sumbu menjadi persen CSS, meneruskan "di luar lembar" apa adanya. */
function mapFraction(t: number | null): number | null {
  return t === null ? null : t * 100;
}

/**
 * Build an SVG path, breaking it at nulls so a gap in the data reads as a gap
 * rather than a straight line bridging it.
 */
/*
 * Sumbu sebuah kurva adalah sumbu **kurva itu**, dan sumbu track hanyalah
 * cadangan ketika kurvanya tidak menyebut sendiri.
 *
 * Dulu urutannya terbalik — track menang — dan akibatnya satu kotak benar-benar
 * hanya punya satu sumbu: RT yang lazimnya logaritmik, ditumpuk di kotak GR
 * yang linear, digambar linear. Bentuknya tetap muncul, tidak ada galat, dan
 * yang terlihat hanyalah resistivitas yang menempel di tepi kiri sepanjang
 * lubang — persis seperti kurva yang datanya buruk.
 *
 * Kedua ujung skala memang sudah milik tiap kurva sejak lama; yang tertinggal
 * hanyalah pilihan linear/logaritmiknya. Yang tetap milik track adalah **kisi
 * dan angka yang tercetak di kepalanya**: satu kotak hanya muat satu deret
 * angka, dan itu deret milik kurva pertama. Kurva selebihnya dibaca dari kedua
 * ujung skala di baris legendanya sendiri — sebagaimana lembar log kertas
 * selalu melakukannya.
 */
export function buildPath(
  curve: TrackCurve,
  top: number,
  base: number,
  logarithmic?: boolean,
): string {
  if (!axisSpan(top, base)) return "";
  const segments: string[] = [];
  let open = false;
  for (let i = 0; i < curve.depths.length; i++) {
    const value = curve.values[i];
    const t = axisFraction(curve.depths[i], top, base);
    if (value === null || !Number.isFinite(value) || t === null) {
      open = false;
      continue;
    }
    const x = project(value, curve.min, curve.max, curve.logarithmic ?? logarithmic);
    if (!Number.isFinite(x)) {
      open = false;
      continue;
    }
    const y = t * VIEW_H;
    segments.push(`${open ? "L" : "M"}${x.toFixed(2)},${y.toFixed(1)}`);
    open = true;
  }
  return segments.join(" ");
}

/**
 * Perhentian gradien yang mengikuti bacaan kurva menurun sepanjang lubang.
 *
 * Dipakai arsiran `by: "value"`. Setiap perhentian duduk di kedudukan tegak
 * sebuah sampel dan membawa nilai sampel itu, dinormalkan pada skala tampilan
 * kurvanya — jadi seluruh lebar arsiran pada kedalaman itu berwarna satu warna:
 * warna bacaannya, sama seperti log terarsir di volume 3D.
 *
 * Jumlahnya dibatasi. Sebuah kurva 0,15 m di jendela 300 m membawa dua ribu
 * sampel, dan dua ribu `<stop>` adalah dua ribu simpul DOM per arsiran per
 * track; pada `MAX_STOPS` perhentian, satu berlaku untuk kurang dari dua
 * piksel pada lembar setinggi layar mana pun. Yang dijarangi hanya sampel yang
 * dilewati — tidak ada yang dirata-rata, jadi warnanya tetap warna bacaan yang
 * memang diambil.
 *
 * Sampel kosong tidak menyumbang perhentian: pada rumpang, arsirannya sendiri
 * tidak digambar, jadi warnanya tidak menyatakan apa pun di sana.
 */
const MAX_STOPS = 256;

/**
 * Kedudukan sebuah nilai di ramp warna, 0 di ujung rendah skala dan 1 di ujung
 * tingginya.
 *
 * Pada sumbu logaritmik yang dihitung adalah dekadenya. Pecahan linear atas
 * RT 0,2–2.000 ohm·m menaruh seluruh 0,2 sampai 200 di sepersepuluh pertama
 * ramp: tiga perempat lebar track tergambar ungu yang sama, dan warnanya baru
 * berubah di ujung kanan — ramp yang tidak mengatakan apa pun tentang dekade
 * yang justru tertulis di skalanya.
 */
export function rampFraction(value: number, a: number, b: number, logarithmic = false): number {
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  if (!(hi > lo)) return 0;
  const f = logarithmic && lo > 0 && value > 0
    ? (Math.log10(value) - Math.log10(lo)) / (Math.log10(hi) - Math.log10(lo))
    : (value - lo) / (hi - lo);
  return Math.min(1, Math.max(0, f));
}

export function valueStops(
  curve: TrackCurve,
  top: number,
  base: number,
  logarithmic = false,
): { offset: number; t: number }[] {
  // Pecahannya dipungut sekalian: sebuah perhentian hanya berarti bersama
  // kedudukannya, dan menghitungnya dua kali di sumbu yang bisa menurun adalah
  // dua kesempatan untuk salah tanda.
  const inside: { i: number; offset: number }[] = [];
  for (let i = 0; i < curve.depths.length; i++) {
    const value = curve.values[i];
    if (value === null || !Number.isFinite(value)) continue;
    const t = axisFraction(curve.depths[i], top, base);
    if (t === null) continue;
    inside.push({ i, offset: t * 100 });
  }
  if (!inside.length) return [];
  const stride = Math.max(1, Math.ceil(inside.length / MAX_STOPS));
  const stops: { offset: number; t: number }[] = [];
  const stopAt = ({ i, offset }: { i: number; offset: number }) => ({
    offset,
    t: rampFraction(curve.values[i] as number, curve.min, curve.max, logarithmic),
  });
  for (let n = 0; n < inside.length; n += stride) stops.push(stopAt(inside[n]));
  const last = inside[inside.length - 1];
  if (stops[stops.length - 1].offset < last.offset) stops.push(stopAt(last));
  return stops;
}

/**
 * Closed polygons between a curve and a vertical baseline, kept to one side.
 *
 * The baseline is given as a fraction of the display scale rather than a value
 * in the curve's own units, so one setting works for GR in gAPI, VSH in
 * fractions and RT on a log axis without three separate special cases.
 *
 * Runs are broken at nulls and at every baseline crossing: a single polygon
 * spanning a crossing would shade rock that sits on the other side of the
 * cutoff, which is exactly the mistake shading is meant to prevent.
 */
export function buildBaselineFill(
  curve: TrackCurve,
  top: number,
  base: number,
  baselineFraction: number,
  side: "left" | "right",
  logarithmic?: boolean,
): string {
  if (!axisSpan(top, base)) return "";
  const bx = baselineFraction * VIEW_W;
  const parts: string[] = [];
  let run: string[] = [];

  const flush = () => {
    if (run.length < 2) {
      run = [];
      return;
    }
    const firstY = run[0].split(",")[1];
    const lastY = run[run.length - 1].split(",")[1];
    parts.push(`M${bx.toFixed(2)},${firstY} L${run.join(" L")} L${bx.toFixed(2)},${lastY} Z`);
    run = [];
  };

  for (let i = 0; i < curve.depths.length; i++) {
    const t = axisFraction(curve.depths[i], top, base);
    const value = curve.values[i];
    if (t === null || value === null || !Number.isFinite(value)) {
      flush();
      continue;
    }
    const x = project(value, curve.min, curve.max, curve.logarithmic ?? logarithmic);
    if (!Number.isFinite(x) || (side === "left" ? x >= bx : x <= bx)) {
      flush();
      continue;
    }
    const y = t * VIEW_H;
    run.push(`${x.toFixed(2)},${y.toFixed(1)}`);
  }
  flush();
  return parts.join(" ");
}

/** Closed polygon between two curves — the net-pay / crossover shading. */
function buildShading(
  a: TrackCurve,
  b: TrackCurve,
  top: number,
  base: number,
  logarithmic?: boolean,
): string {
  if (!axisSpan(top, base)) return "";
  const forward: string[] = [];
  const backward: string[] = [];
  for (let i = 0; i < a.depths.length; i++) {
    const t = axisFraction(a.depths[i], top, base);
    if (t === null) continue;
    const av = a.values[i];
    const bv = b.values[i];
    if (av === null || bv === null || !Number.isFinite(av) || !Number.isFinite(bv)) continue;
    const ax = project(av, a.min, a.max, logarithmic);
    const bx = project(bv, b.min, b.max, logarithmic);
    if (!Number.isFinite(ax) || !Number.isFinite(bx) || ax <= bx) continue;
    const y = t * VIEW_H;
    forward.push(`${ax.toFixed(2)},${y.toFixed(1)}`);
    backward.unshift(`${bx.toFixed(2)},${y.toFixed(1)}`);
  }
  if (!forward.length) return "";
  return `M${forward.join(" L")} L${backward.join(" L")} Z`;
}

/**
 * Kepala track — nama, satuan, legenda kurva, dan skala mendatarnya.
 *
 * Skala itu yang membuat track bisa dibaca sebagai pengukuran alih-alih
 * sebagai gambar. Setiap kurva menunjukkan kedua ujung skalanya sendiri di
 * kiri dan kanan garis legendanya, sehingga sebuah kurva yang mepet ke tepi
 * kanan track bisa langsung dibaca sebagai "sudah di 2000 ohm·m" dan bukan
 * sekadar "tinggi". Di bawahnya, satu penggaris mencetak nilai di setiap garis
 * kisi — pada sumbu logaritmik itu berarti dekade, bukan pembagian rata.
 */
export function TrackHeaders({
  tracks,
  gutter = DEPTH_GUTTER_W,
  height = 96,
  light,
  depthUnit = "MD m",
  gridDivisions,
  onSelect,
  selected,
  weight = 1.3,
  onCurveEdit,
  onCurveAuto,
  onSpectrumEdit,
  onSpectrumRemove,
  onScaleToggle,
  onReorder,
  scrollX = 0,
}: {
  tracks: TrackSpec[];
  gutter?: number;
  height?: number;
  light?: boolean;
  /** Axis caption, e.g. "MD m" or "TVDSS ft". */
  depthUnit?: string;
  /** Pembagian sumbu linear, disamakan dengan kisi yang tergambar di track. */
  gridDivisions?: number;
  onSelect?: (id: string) => void;
  selected?: string | null;
  /** Ketebalan lembar, dipakai kotak sunting kurva tanpa ketebalan sendiri. */
  weight?: number;
  /** Menyunting satu kurva dari kepalanya. Kosong berarti kepala hanya dibaca —
   *  yang benar untuk lembar cetak, tempat tidak ada yang bisa diklik. */
  /** Kurva disebut lewat `TrackCurve.key`, bukan namanya; lihat medan itu. */
  onCurveEdit?: (trackId: string, curveKey: string, patch: Partial<CurveConfig>) => void;
  /** Memulangkan satu kurva ke skala tampilannya sendiri. */
  onCurveAuto?: (trackId: string, curveKey: string) => void;
  onSpectrumEdit?: (trackId: string, patch: Partial<SpectrumConfig>) => void;
  onSpectrumRemove?: (trackId: string) => void;
  /** Menukar sumbu mendatar track antara linear dan logaritmik dari kepalanya.
   *  Kosong berarti lencana LIN/LOG hanya sebuah keterangan, sebagaimana pada
   *  lembar yang susunan tracknya disunting di tempat lain. */
  onScaleToggle?: (trackId: string) => void;
  /**
   * Geseran mendatar lembar di bawahnya, dalam piksel.
   *
   * Kepala track digeser oleh nilai ini alih-alih memiliki batang gulirnya
   * sendiri. Dua batang gulir untuk satu lembar berarti dua sumber kebenaran
   * yang harus disamakan terus-menerus, dan kepala yang meleset satu piksel
   * dari tracknya adalah kepala yang berbohong tentang skala.
   */
  scrollX?: number;
  /**
   * Menyusun ulang track dengan menyeret kepalanya, dengan id dan bukan nomor
   * urut: yang tergambar di sini adalah track yang punya kurva, sedangkan yang
   * disusun ulang adalah seluruh susunannya — termasuk track yang kurvanya
   * kebetulan tidak ada di sumur ini.
   */
  onReorder?: (id: string, targetId: string) => void;
}) {
  const reorder = useReorderDrag((from, to) => {
    const moved = tracks[from];
    const target = tracks[to];
    if (moved && target) onReorder?.(moved.id, target.id);
  });
  return (
    <div
      style={{
        display: "flex",
        alignItems: "stretch",
        // Tinggi terkecil, bukan tinggi tetap: track berkurva empat harus
        // mendorong kepalanya lebih tinggi alih-alih menumpahkan barisnya ke
        // atas kurva di bawahnya.
        minHeight: height,
        flex: "none",
        background: light ? "var(--panel)" : "var(--toolbar)",
        borderBottom: "1px solid var(--line)",
        // Kotak sunting menggantung dari baris ini ke atas lembar di bawahnya.
        // Geseran mendatar membuat baris ini sebuah konteks penumpukan
        // tersendiri, dan tanpa lapis di sini kotak itu tertinggal di
        // belakang tracknya — terbuka, tetapi tidak bisa diklik.
        position: "relative",
        zIndex: 5,
      }}
    >
      <div
        style={{
          width: gutter,
          flex: "none",
          borderRight: "1px solid var(--line)",
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          paddingBottom: 6,
          // Kepala track yang tergeser lewat di bawah sel ini, bukan di
          // atasnya: sel mistar harus tetap terbaca pada geseran berapa pun.
          position: "relative",
          zIndex: 1,
          background: "inherit",
        }}
      >
        <span style={{ font: "600 9.5px var(--font-mono)", color: "var(--ink-4)" }}>{depthUnit}</span>
      </div>
      {/* Digeser, bukan dikliping: kotak sunting kurva menggantung keluar dari
          baris kepala, dan sebuah overflow:hidden di sini akan memotongnya.
          Yang menahan tumpahannya adalah panel di luar sana, yang memang
          sudah mengkliping. */}
      <div
        style={{
          flex: 1,
          // Selebar tracknya sendiri, bukan selebar ruang yang tersisa:
          // lembar di bawah sini boleh tumbuh melampaui panelnya, dan kepala
          // yang berhenti di tepi panel akan meleset dari track keenam ke
          // kanan justru ketika geseran mendatar mulai berguna.
          minWidth: tracksMinWidth(tracks),
          display: "flex",
          alignItems: "stretch",
          transform: `translateX(${-scrollX}px)`,
        }}
        {...(onReorder ? reorder.rowProps : {})}
      >
      {tracks.map((track, index) => (
        <TrackHeader
          key={track.id}
          track={track}
          dragProps={onReorder ? reorder.itemProps(index) : undefined}
          dragging={reorder.dragging === index}
          dropEdge={
            reorder.dragging === null || reorder.dropIndex !== index || reorder.dragging === index
              ? null
              : reorder.dropIndex < reorder.dragging
                ? "left"
                : "right"
          }
          gridDivisions={gridDivisions}
          onSelect={onSelect}
          selected={selected === track.id}
          weight={weight}
          // Dua track terakhir menggantungkan kotak suntingnya ke kiri: sebuah
          // kotak selebar 176 piksel yang lahir di tepi kanan lembar keluar
          // dari layar, dan tidak ada yang menggulirnya kembali.
          align={index >= tracks.length - 2 ? "right" : "left"}
          onCurveEdit={onCurveEdit}
          onCurveAuto={onCurveAuto}
          onSpectrumEdit={onSpectrumEdit}
          onSpectrumRemove={onSpectrumRemove}
          onScaleToggle={onScaleToggle}
        />
      ))}
      </div>
    </div>
  );
}

/**
 * Kepala satu track: judul, satuan, sumbu, dan satu baris legenda per kurva.
 *
 * Diekspor karena lembar korelasi memakai kepala yang sama persis. Sebuah
 * kepala kedua yang ditulis sendiri di sana akan berarti dua tempat yang harus
 * sepakat tentang di mana sebuah skala tercetak dan apa yang terjadi ketika
 * baris legendanya diklik — dan dua tempat seperti itu selalu berselisih.
 */
export function TrackHeader({
  track,
  gridDivisions,
  onSelect,
  selected,
  weight = 1.3,
  align = "left",
  onCurveEdit,
  onCurveAuto,
  onSpectrumEdit,
  onSpectrumRemove,
  onScaleToggle,
  minWidth = TRACK_MIN_PX,
  dragProps,
  dragging,
  dropEdge,
}: {
  track: TrackSpec;
  gridDivisions?: number;
  onSelect?: (id: string) => void;
  selected?: boolean;
  weight?: number;
  align?: "left" | "right";
  /** Kurva disebut lewat `TrackCurve.key`, bukan namanya; lihat medan itu. */
  onCurveEdit?: (trackId: string, curveKey: string, patch: Partial<CurveConfig>) => void;
  onCurveAuto?: (trackId: string, curveKey: string) => void;
  onSpectrumEdit?: (trackId: string, patch: Partial<SpectrumConfig>) => void;
  onSpectrumRemove?: (trackId: string) => void;
  onScaleToggle?: (trackId: string) => void;
  /**
   * Lebar terkecil satu satuan flex, dalam piksel.
   *
   * Lembar log memakai lantai ini supaya track kesembilan tidak memampatkan
   * delapan lainnya; lembar korelasi menyerahkan nol, sebab di sana yang harus
   * muat berdampingan adalah sumur-sumurnya, dan sebuah panel yang menolak
   * menyempit membuat traverse enam sumur tidak bisa dipandang sekaligus.
   */
  minWidth?: number;
  /**
   * Pegangan seret dari `useReorderDrag`, ditaruh apa adanya pada kepala ini.
   * Kosong berarti kepala yang tidak bisa disusun ulang — lembar cetak, dan
   * lembar mana pun yang susunannya bukan miliknya.
   */
  dragProps?: Record<string, unknown>;
  /** Kepala yang sedang dibawa tangan. */
  dragging?: boolean;
  /** Sisi tempat kepala yang dibawa akan mendarat, digambar sebagai garis. */
  dropEdge?: "left" | "right" | null;
}) {
  // Kotak sunting yang sedang terbuka: nama kurva, atau `"__spectrum"` untuk
  // pita warna. Satu saja pada satu saat — dua kotak yang saling menindih di
  // atas lembar yang sama menutupi justru kurva yang sedang disetel.
  const [editing, setEditing] = useState<string | null>(null);
  const editable = onCurveEdit !== undefined;
  const scale: AxisScale = track.logarithmic ? "log" : "linear";
  // Penggaris mengikuti kurva pertama track: pada track dua-kurva seperti
  // RHOB/NPHI keduanya memang dibaca terhadap kisi yang sama, dan mencetak dua
  // deret angka di ruang selebar satu track hanya membuat keduanya tak terbaca.
  const ruler = track.curves[0];
  const ticks = useMemo(
    () =>
      ruler
        ? axisTicks(ruler.min, ruler.max, scale, gridDivisions ?? (scale === "log" ? 4 : 5))
            .filter((tick) => tick.major)
        : [],
    [ruler, scale, gridDivisions],
  );

  // Angka skala dibuang ketika dua di antaranya bertumpang tindih — lihat
  // `thinScaleLabels`. Lebarnya diukur dan bukan ditebak dari `track.flex`:
  // flex adalah bagian dari sisa ruang, dan sisa ruang itu berbeda pada lembar
  // enam belas kolom, pada lembar cetak A3, dan pada panel korelasi yang
  // dilipat. Render pertama menggambar semuanya lalu menyusut sekali begitu
  // ukurannya tiba; membiarkannya sesak satu frame lebih baik daripada
  // menyembunyikan angka atas lebar yang belum diketahui.
  const [tickRef, tickBox] = useElementSize<HTMLDivElement>();
  const drawnTicks = useMemo(
    () =>
      thinScaleLabels(
        ticks.map((tick) => ({ t: tick.t, text: formatScaleValue(tick.value) })),
        tickBox.width,
      ),
    [ticks, tickBox.width],
  );

  return (
    <div
      onClick={onSelect ? () => onSelect(track.id) : undefined}
      {...dragProps}
      style={{
        flex: track.flex,
        minWidth: track.flex * minWidth,
        borderRight: "1px solid var(--line)",
        borderTop: `2px solid ${selected ? "var(--accent)" : "transparent"}`,
        background: selected ? "var(--accent-wash)" : "transparent",
        padding: "4px 6px 3px",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        // Yang bisa diseret mengaku bisa diseret sebelum disentuh: sebuah
        // kepala yang hanya berubah setelah ditarik adalah kemampuan yang
        // ditemukan secara kebetulan.
        cursor: dragProps ? (dragging ? "grabbing" : "grab") : onSelect ? "pointer" : "default",
        // Yang dibawa tangan dibuat pudar, dan tempat mendaratnya diberi garis
        // di sisi yang benar — tanpa keduanya, sebuah seretan sepanjang lima
        // track adalah lima track yang berkedip tanpa mengatakan ke mana yang
        // dipegang akan jatuh.
        opacity: dragging ? 0.5 : 1,
        boxShadow: dropEdge
          ? `inset ${dropEdge === "left" ? "" : "-"}3px 0 0 0 var(--accent)`
          : undefined,
        ...(dragProps?.style as React.CSSProperties | undefined),
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 6 }}>
        <span
          style={{
            font: "600 10px var(--font-sans)",
            color: "var(--ink)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={track.title}
        >
          {track.title}
        </span>
        <span style={{ display: "flex", alignItems: "baseline", gap: 5, flex: "none" }}>
          <span style={{ font: "400 9px var(--font-mono)", color: "var(--ink-6)" }}>{track.units}</span>
          {/* Lencana sumbu. Ia menjadi tombolnya sendiri di lembar yang tidak
              punya penyunting susunan track di rel kanan: sebuah kurva
              resistivitas pada sumbu linear adalah garis yang menempel di tepi
              kiri, dan tidak ada gunanya memperlihatkan bahwa sumbunya salah
              tanpa memberi jalan membetulkannya.

              Kolom tanpa kurva tidak punya sumbu mendatar sama sekali — pita
              kelas, bendera, litologi, uji produksi — jadi lencananya dibuang
              di sana alih-alih berbunyi "LIN" tentang sumbu yang tidak ada.
              Kolomnya juga yang paling sempit di lembar, dan tiga belas piksel
              yang dipakai lencana itu justru yang dibutuhkan judulnya. */}
          {track.curves.length > 0 && (
          <span
            role={onScaleToggle ? "button" : undefined}
            tabIndex={onScaleToggle ? 0 : undefined}
            aria-label={onScaleToggle ? `Axis of ${track.title}` : undefined}
            aria-pressed={onScaleToggle ? scale === "log" : undefined}
            title={
              onScaleToggle
                ? scale === "log"
                  ? "Logarithmic scale — click for linear"
                  : "Linear scale — click for logarithmic"
                : scale === "log"
                  ? "Logarithmic scale"
                  : "Linear scale"
            }
            onClick={
              onScaleToggle
                ? (event) => {
                    event.stopPropagation();
                    onScaleToggle(track.id);
                  }
                : undefined
            }
            onKeyDown={
              onScaleToggle
                ? (event) => {
                    if (event.key !== "Enter" && event.key !== " ") return;
                    event.preventDefault();
                    event.stopPropagation();
                    onScaleToggle(track.id);
                  }
                : undefined
            }
            style={{
              font: "600 8px var(--font-mono)",
              letterSpacing: ".05em",
              color: scale === "log" ? "var(--amber)" : "var(--ink-6)",
              border: `1px solid ${scale === "log" ? "var(--amber)" : "var(--line-strong)"}`,
              borderRadius: 3,
              padding: "0 3px",
              cursor: onScaleToggle ? "pointer" : undefined,
            }}
          >
            {scale === "log" ? "LOG" : "LIN"}
          </span>
          )}
        </span>
      </div>

      {track.curves.map((curve, index) => {
        // Nama hanya tulisan di legenda; yang dipakai membedakan dan
        // mengalamatkan adalah identitasnya. Kedudukan dipakai ketika lembar
        // ini tidak memberi identitas apa pun, supaya dua kurva bernama sama
        // tetap membuka kotaknya masing-masing alih-alih membuka keduanya.
        const id = curve.key ?? `${index}·${curve.name}`;
        const open = editing === id;
        const rule = `${(curve.width ?? 2).toFixed(1)}px ${dashStyleName(curveStyleOf(curve))} ${curve.color}`;
        return (
          <div key={id} style={{ position: "relative" }}>
            <div
              // Baris legenda itu sendiri yang menjadi tombolnya. Sebuah ikon
              // pensil di sebelahnya akan memakan lebar yang justru dibutuhkan
              // kedua angka skala, dan baris ini sudah memperlihatkan persis
              // apa yang akan disetel: warnanya, bentuk garisnya, kedua ujung
              // skalanya.
              role={editable ? "button" : undefined}
              tabIndex={editable ? 0 : undefined}
              aria-label={editable ? `Set up curve ${curve.name}` : undefined}
              aria-expanded={editable ? open : undefined}
              title={editable ? `Set the colour and scale of ${curve.name}` : undefined}
              onClick={
                editable
                  ? (event) => {
                      event.stopPropagation();
                      setEditing(open ? null : id);
                    }
                  : undefined
              }
              onKeyDown={
                editable
                  ? (event) => {
                      if (event.key !== "Enter" && event.key !== " ") return;
                      event.preventDefault();
                      event.stopPropagation();
                      setEditing(open ? null : id);
                    }
                  : undefined
              }
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                borderRadius: 3,
                outline: open ? "1px solid var(--accent)" : undefined,
                cursor: editable ? "pointer" : undefined,
              }}
            >
              <span style={{ font: "400 8.5px var(--font-mono)", color: "var(--ink-6)", flex: "none" }}>
                {formatScaleValue(curve.min)}
              </span>
              <span style={{ flex: 1, minWidth: 8, height: 0, borderTop: rule }} />
              <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-3)", flex: "none" }}>
                {curve.name}
              </span>
              <span style={{ flex: 1, minWidth: 8, height: 0, borderTop: rule }} />
              <span style={{ font: "400 8.5px var(--font-mono)", color: "var(--ink-6)", flex: "none" }}>
                {curve.rangeLabel ?? formatScaleValue(curve.max)}
              </span>
            </div>
            {open && onCurveEdit && (
              <CurveQuickEdit
                name={curve.name}
                color={curve.color}
                style={curveStyleOf(curve)}
                width={curve.width ?? weight}
                min={curve.min}
                max={curve.max}
                custom={curve.customScale === true}
                fill={curve.fill}
                align={align}
                onChange={(patch) => onCurveEdit(track.id, id, patch)}
                onAuto={() => onCurveAuto?.(track.id, id)}
                onClose={() => setEditing(null)}
              />
            )}
          </div>
        );
      })}

      {/* Pita warna: strip gradien beserta kedua ujung jepitannya, yang juga
          merupakan tombolnya sendiri. Sebuah bar warna tanpa angka hanya
          mengatakan bahwa sebagian batuan berbeda dari sebagian yang lain. */}
      {track.spectrum && (
        <SpectrumHeadBar
          spectrum={track.spectrum}
          open={editing === SPECTRUM_KEY}
          editable={onSpectrumEdit !== undefined}
          align={align}
          onToggle={() => setEditing(editing === SPECTRUM_KEY ? null : SPECTRUM_KEY)}
          onChange={(patch) => onSpectrumEdit?.(track.id, patch)}
          onRemove={() => {
            setEditing(null);
            onSpectrumRemove?.(track.id);
          }}
          onClose={() => setEditing(null)}
        />
      )}

      {ticks.length > 0 && (
        <div ref={tickRef}
             style={{ position: "relative", height: 11, marginTop: "auto", flex: "none" }}>
          {drawnTicks.map((tick) => (
            <span
              key={tick.t}
              style={{
                position: "absolute",
                left: `${tick.t * 100}%`,
                top: 0,
                // Angka pertama dan terakhir dijaga tetap di dalam track;
                // yang di tengah dipusatkan pada garis kisinya.
                transform:
                  tick.t <= 0.001
                    ? "none"
                    : tick.t >= 0.999
                      ? "translateX(-100%)"
                      : "translateX(-50%)",
                font: "400 8px var(--font-mono)",
                color: "var(--ink-7)",
                whiteSpace: "nowrap",
              }}
            >
              {tick.text}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/** Padanan CSS untuk bentuk garis, dipakai pada garis legenda. */
function dashStyleName(style: CurveStyle): string {
  return style === "dotted" ? "dotted" : style === "solid" ? "solid" : "dashed";
}

/** Kunci kotak sunting pita warna, di samping nama-nama kurva. */
const SPECTRUM_KEY = "__spectrum";

/**
 * Bar warna di kepala track, yang sekaligus menjadi tombol setelannya.
 *
 * Barnya membawa kedua ujung jepitannya sendiri. Itu bukan hiasan: sebuah pita
 * warna tanpa angka hanya mengatakan bahwa sebagian batuan berbeda dari
 * sebagian yang lain, dan seluruh gunanya justru ada pada di mana batas antara
 * dua warna itu jatuh.
 */
function SpectrumHeadBar({
  spectrum,
  open,
  editable,
  align,
  onToggle,
  onChange,
  onRemove,
  onClose,
}: {
  spectrum: TrackSpectrum;
  open: boolean;
  editable: boolean;
  align: "left" | "right";
  onToggle: () => void;
  onChange: (patch: Partial<SpectrumConfig>) => void;
  onRemove: () => void;
  onClose: () => void;
}) {
  return (
    <div style={{ position: "relative", marginTop: 1 }}>
      <div
        role={editable ? "button" : undefined}
        tabIndex={editable ? 0 : undefined}
        aria-label={editable ? `Set up the colour band ${spectrum.curve}` : undefined}
        aria-expanded={editable ? open : undefined}
        title={editable ? `Set the ramp and clamps of band ${spectrum.curve}` : undefined}
        onClick={
          editable
            ? (event) => {
                event.stopPropagation();
                onToggle();
              }
            : undefined
        }
        onKeyDown={
          editable
            ? (event) => {
                if (event.key !== "Enter" && event.key !== " ") return;
                event.preventDefault();
                event.stopPropagation();
                onToggle();
              }
            : undefined
        }
        style={{
          borderRadius: 3,
          outline: open ? "1px solid var(--accent)" : undefined,
          cursor: editable ? "pointer" : undefined,
        }}
      >
        <ColorBar
          property={spectrum.curve}
          // Jepitannya diserahkan sebagai rentang data, bukan sebagai clamp:
          // angka yang tercetak lalu tetap angka yang benar-benar dipakai
          // mewarnai, tanpa kata "clamped" yang tidak muat di kolom selebar
          // sembilan puluh piksel. Yang membedakan jepitan sendiri dari
          // jepitan warisan ada di kotak setelannya.
          scale={{ ramp: spectrum.ramp, reverse: spectrum.reverse, min: null, max: null }}
          dataMin={spectrum.lo}
          dataMax={spectrum.hi}
          compact
          minWidth={0}
        />
      </div>
      {open && (
        <SpectrumQuickEdit
          curveName={spectrum.curve}
          ramp={spectrum.ramp}
          reverse={spectrum.reverse}
          bars={spectrum.bars}
          lo={spectrum.lo}
          hi={spectrum.hi}
          custom={spectrum.custom}
          align={align}
          onChange={onChange}
          onRemove={onRemove}
          onClose={onClose}
        />
      )}
    </div>
  );
}

export function DepthGutter({
  top,
  base,
  ticks = 11,
  width = DEPTH_GUTTER_W,
  style,
  cursor = null,
  cursorLabel,
  label,
  onStretch,
}: {
  top: number;
  base: number;
  ticks?: number;
  width?: number;
  style?: CSSProperties;
  /** Kedalaman yang sedang dibaca picker, dicetak di posisinya. */
  cursor?: number | null;
  cursorLabel?: (depth: number) => string;
  /**
   * Cetakan angka pada sumbunya. Bawaannya kedalaman bulat, yang benar untuk
   * lembar log; lembar korelasi yang diratakan pada datum membaca simpangan,
   * dan simpangan tanpa tandanya tidak bisa dibedakan dari kedalaman.
   */
  label?: (depth: number) => string;
  /**
   * Seret tegak pada mistar untuk meregang dan memampatkan skala.
   *
   * `dy` adalah jarak seret dari titik tekan dalam piksel layar, positif ke
   * bawah, dan `clientY` posisi tekan itu sendiri. Yang dilaporkan adalah
   * gerakannya, bukan skalanya: mistar tidak tahu apa-apa tentang skala
   * kedalaman, dan tidak perlu tahu — yang memilikinya adalah layar yang
   * memasangnya, dan hanya layar itu yang tahu tinggi panelnya.
   *
   * Kosong berarti mistar hanya dibaca, yang benar untuk lembar cetak.
   */
  onStretch?: (dy: number, phase: "start" | "move" | "end", clientY: number) => void;
}) {
  // Titik tekan seret, dalam koordinat layar. Layar, bukan koordinat mistar:
  // meregang menggeser mistarnya sendiri di bawah jari, dan jarak yang diukur
  // terhadap sesuatu yang ikut bergerak akan memberi umpan balik ke dirinya
  // sendiri sampai skalanya lari ke salah satu ujung rentang.
  const grab = useRef<number | null>(null);
  const release = (clientY: number) => {
    if (grab.current === null) return;
    const dy = clientY - grab.current;
    grab.current = null;
    onStretch?.(dy, "end", clientY);
  };
  const labels = useMemo(
    () => Array.from({ length: ticks }, (_, i) => top + ((base - top) * i) / (ticks - 1)),
    [top, base, ticks],
  );
  const cursorAt = cursor === null ? null : mapFraction(axisFraction(cursor, top, base));
  return (
    <div
      title={onStretch ? "Drag vertically to stretch or compress the scale" : undefined}
      onPointerDown={
        onStretch
          ? (event) => {
              if (event.button !== 0) return;
              // Mencegah pilihan teks: seret sepanjang mistar yang menyorot
              // sebelas angka kedalaman sekaligus terlihat seperti kerusakan.
              event.preventDefault();
              // Dijaga: bukan setiap lingkungan yang menggambar komponen ini
              // punya penangkapan pointer.
              event.currentTarget.setPointerCapture?.(event.pointerId);
              grab.current = event.clientY;
              onStretch(0, "start", event.clientY);
            }
          : undefined
      }
      onPointerMove={
        onStretch
          ? (event) => {
              if (grab.current === null) return;
              onStretch(event.clientY - grab.current, "move", event.clientY);
            }
          : undefined
      }
      onPointerUp={onStretch ? (event) => release(event.clientY) : undefined}
      onPointerCancel={onStretch ? (event) => release(event.clientY) : undefined}
      style={{
        position: "relative",
        width,
        flex: "none",
        borderRight: "1px solid var(--line)",
        display: "flex",
        flexDirection: "column",
        background: "var(--canvas-gutter)",
        cursor: onStretch ? "ns-resize" : undefined,
        touchAction: onStretch ? "none" : undefined,
        userSelect: onStretch ? "none" : undefined,
        ...style,
      }}
    >
      {labels.map((depth, index) => (
        <div
          key={index}
          style={{
            flex: 1,
            borderTop: "1px solid var(--line-soft)",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "flex-end",
            padding: "2px 7px 0 0",
          }}
        >
          <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-5)" }}>
            {label ? label(depth) : depth.toFixed(0)}
          </span>
        </div>
      ))}
      {cursorAt !== null && (
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: `${cursorAt}%`,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            pointerEvents: "none",
            borderTop: "1px solid var(--accent)",
          }}
        >
          <span
            style={{
              transform: "translateY(-50%)",
              margin: "0 4px",
              padding: "0 4px",
              borderRadius: 3,
              background: "var(--accent)",
              color: "var(--accent-contrast)",
              font: "600 9.5px var(--font-mono)",
              whiteSpace: "nowrap",
            }}
          >
            {cursorLabel ? cursorLabel(cursor as number) : (cursor as number).toFixed(1)}
          </span>
        </div>
      )}
    </div>
  );
}

export function LogTrack({
  track,
  top,
  base,
  zones = [],
  markers = [],
  showGrid = true,
  gridDivisions,
  depthGrid = DEPTH_GRID_PX,
  weight = 1.3,
  lithologyBlocks,
  describedBlocks,
  classBlocks,
  emptyNote,
  onHoverDepth,
  cursorDepth = null,
  showReadings = true,
  onPickDepth,
  minWidth = TRACK_MIN_PX,
}: {
  track: TrackSpec;
  top: number;
  base: number;
  zones?: ZoneBand[];
  markers?: MarkerLine[];
  showGrid?: boolean;
  /** Vertical grid lines across the track. Defaults to 5 on a linear scale and
   *  a decade grid on a logarithmic one, which is what log paper does. */
  gridDivisions?: number;
  /** Horizontal grid spacing in pixels. */
  depthGrid?: number;
  /** Ketebalan bawaan; sebuah kurva boleh membawa ketebalannya sendiri. */
  weight?: number;
  lithologyBlocks?: { top: number; base: number; fill: string }[];
  describedBlocks?: { top: number; base: number; fill: string; label: string; hint: string }[];
  /** Pita kelas LRLC; lihat `lib/lrlc.classBlocks`. Juga dipakai kolom pita
   *  lain — bendera dan uji produksi — yang bentuknya sama persis. */
  classBlocks?: { top: number; base: number; code: number; fill: string; label: string }[];
  /** Kalimat ketika kolom pita ini kosong. Kosong berarti kalimat kelas LRLC. */
  emptyNote?: string;
  onHoverDepth?: (depth: number | null) => void;
  /** Kedalaman yang sedang dibaca — garis bidik dan angkanya. */
  cursorDepth?: number | null;
  showReadings?: boolean;
  /** Klik pada track: mengunci atau melepas kuncian picker. */
  onPickDepth?: (depth: number) => void;
  /** Lebar terkecil satu satuan flex, dalam piksel; lihat `TrackHeader`. */
  minWidth?: number;
}) {
  const span = base - top;
  const paths = useMemo(
    () =>
      track.curves.map((curve, index) => ({
        curve,
        index,
        d: buildPath(curve, top, base, track.logarithmic),
      })),
    [track, top, base],
  );
  /**
   * Satu arsiran untuk setiap kurva yang meminta, digambar sebelum garisnya.
   *
   * Acuannya adalah ujung *nilai*, bukan tepi track. Pada sumbu biasa keduanya
   * sama, tetapi `curve.min` dan `curve.max` menamai ujung kiri dan ujung
   * kanan skala tampilan — dan pada sumbu yang dibalik, yang di kanan justru
   * angka terkecil. NPHI 0,45 → −0,15 dan SW 1 → 0 dibaca terbalik justru
   * sebagai kelaziman, jadi menganggap tepi kanan selalu berarti nilai
   * tertinggi akan mengarsir sisi yang berlawanan dengan yang diminta pada dua
   * kurva yang paling sering diarsir orang.
   */
  // Satu awalan per track, dipegang di ref supaya tetap sama antar render:
  // pengenal yang berubah setiap render akan memutus rujukan `url(#…)` yang
  // sudah dipasang di atribut fill.
  const gradientPrefix = useRef<string>("");
  if (!gradientPrefix.current) gradientPrefix.current = `arsir${(gradientSeq += 1)}`;
  const gradientId = (index: number, name: string) =>
    `${gradientPrefix.current}-${index}-${name.replace(/[^A-Za-z0-9_-]/g, "")}`;

  const fills = useMemo(
    () =>
      track.curves
        // Kedudukannya diambil sebelum penyaringan: ia harus menunjuk kurva di
        // dalam `track.curves`, bukan urutan di dalam daftar arsiran.
        .map((curve, index) => ({ curve, index }))
        .filter(({ curve }) => curve.fill)
        .map(({ curve, index }) => {
          // Ujung yang dituju berada di tepi kanan bila sumbunya biasa dan
          // arsirannya menuju nilai tertinggi, atau bila keduanya terbalik.
          const atRight = (curve.fill!.from === "max") !== (curve.max < curve.min);
          const by = curve.fill!.by ?? "across";
          const stops = by === "value" ? valueStops(curve, top, base, track.logarithmic) : [];
          return {
            index,
            name: curve.name,
            color: curve.fill!.color ?? curve.color,
            // Gradien tanpa satu perhentian pun tidak mewarnai apa pun, dan
            // luasan yang hilang terbaca sebagai data yang hilang. Bila tidak
            // ada bacaan untuk mewarnainya, arsirannya kembali berwarna tunggal.
            ramp: by === "value" && !stops.length ? undefined : curve.fill!.ramp,
            reverse: curve.fill!.reverse === true,
            by,
            stops,
            min: curve.min,
            max: curve.max,
            d: buildBaselineFill(
              curve,
              top,
              base,
              atRight ? 1 : 0,
              atRight ? "left" : "right",
              track.logarithmic,
            ),
          };
        })
        .filter((fill) => fill.d),
    [track, top, base],
  );

  /** Sisi seberang arsiran yang memintanya — lihat `CurveFill.opposite`. */
  const opposites = useMemo(
    () =>
      track.curves
        .map((curve, index) => ({ curve, index }))
        .filter(({ curve }) => curve.fill?.opposite)
        .map(({ curve, index }) => {
          const atRight = (curve.fill!.from === "max") !== (curve.max < curve.min);
          return {
            index,
            name: curve.name,
            color: curve.fill!.opposite!,
            d: buildBaselineFill(
              curve,
              top,
              base,
              atRight ? 0 : 1,
              atRight ? "right" : "left",
              track.logarithmic,
            ),
          };
        })
        .filter((fill) => fill.d),
    [track, top, base],
  );

  /**
   * Garis kisi tegak, di tempat angkanya benar-benar jatuh.
   *
   * Kisi lama adalah gradien berulang berjarak sama — benar pada sumbu linear,
   * dan keliru pada sumbu logaritmik, tempat jarak 0,2 ke 2 dan 2 ke 20 sama
   * lebarnya di kertas tetapi tidak pernah sama lebarnya pada kisi rata. Yang
   * dihitung di sini adalah dekade sungguhan, memakai fungsi yang sama dengan
   * penggaris di kepala track, sehingga garis dan angkanya tidak bisa berselisih.
   */
  const gridTicks = useMemo(() => {
    const scale: AxisScale = track.logarithmic ? "log" : "linear";
    const ruler = track.curves[0];
    if (!ruler) return [];
    return axisTicks(ruler.min, ruler.max, scale, gridDivisions ?? (scale === "log" ? 4 : 5));
  }, [track, gridDivisions]);

  /** Bacaan setiap kurva pada kedalaman picker, berikut letaknya di track. */
  const readings = useMemo(() => {
    if (cursorDepth === null || axisFraction(cursorDepth, top, base) === null) return [];
    return track.curves
      .map((curve, index) => {
        const value = sampleAt(curve, cursorDepth);
        if (value === null) return null;
        const x = project(value, curve.min, curve.max, curve.logarithmic ?? track.logarithmic);
        return {
          curve,
          index,
          value,
          t: Number.isFinite(x) ? Math.min(1, Math.max(0, x / VIEW_W)) : null,
        };
      })
      .filter(
        (entry): entry is { curve: TrackCurve; index: number; value: number; t: number | null } =>
          entry !== null,
      );
  }, [track, cursorDepth, top, base, span]);

  const cursorAt = cursorDepth === null ? null : mapFraction(axisFraction(cursorDepth, top, base));

  /**
   * Pita warna: satu kotak per kotak kedalaman, diwarnai oleh nilainya sendiri.
   *
   * Penjarangan ke 400 kotak dilakukan di `buildSpectrumBars`. Sebuah log
   * 20.000 sampel yang digambar satu sampel sekali menjadi 20.000 elemen SVG
   * pada satu track; dijarangkan, ia tidak terlihat berbeda di layar mana pun
   * yang tingginya kurang dari 400 piksel per track.
   */
  const versiRamp = useRampsVersion();
  const spectrumBars = useMemo(() => {
    const spec = track.spectrum;
    if (!spec) return null;
    const curve = track.curves.find((c) => c.name === spec.curve) ?? track.curves[0];
    if (!curve) return null;
    const painter = makePainter(
      { ramp: spec.ramp, reverse: spec.reverse, min: null, max: null },
      spec.lo,
      spec.hi,
    );
    // Kurvanya dipinjam dengan kedua ujung skala diganti jepitan pita, supaya
    // posisi warna dihitung terhadap jepitan itu — bukan terhadap skala
    // mendatar track, yang boleh saja berbeda dan sering memang berbeda.
    const clamped: TrackCurve = { ...curve, min: spec.lo, max: spec.hi };
    return buildSpectrumBars(clamped, top, base, 400, track.logarithmic).map((bar) => ({
      ...bar,
      fill: painter.atFraction(bar.t),
    }));
    // `versiRamp`: warna palet yang disunting di tempat ikut tergambar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track, top, base, versiRamp]);

  const shading = useMemo(() => {
    if (!track.shading) return null;
    const a = track.curves.find((c) => c.name === track.shading!.curve);
    const b = track.curves.find((c) => c.name === track.shading!.against);
    if (!a || !b) return null;
    return { d: buildShading(a, b, top, base, track.logarithmic), color: track.shading.color };
  }, [track, top, base]);

  return (
    <div
      // Dibaca oleh menu klik-kanan lembar log: menu itu duduk di wadah di
      // atas track ini, dan tanpa penanda ini ia tidak tahu kurva mana yang
      // sedang ditunjuk jari.
      data-track-id={track.id}
      style={{
        position: "relative",
        flex: track.flex,
        minWidth: track.flex * minWidth,
        borderRight: "1px solid var(--line)",
        background: "var(--canvas-deep)",
        overflow: "hidden",
      }}
      onMouseMove={
        onHoverDepth
          ? (event) => {
              const rect = event.currentTarget.getBoundingClientRect();
              onHoverDepth(top + ((event.clientY - rect.top) / rect.height) * span);
            }
          : undefined
      }
      onMouseLeave={onHoverDepth ? () => onHoverDepth(null) : undefined}
      onClick={
        onPickDepth
          ? (event) => {
              const rect = event.currentTarget.getBoundingClientRect();
              onPickDepth(top + ((event.clientY - rect.top) / rect.height) * span);
            }
          : undefined
      }
    >
      {showGrid && (
        <>
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage: `repeating-linear-gradient(to bottom, var(--grid-line) 0 1px, transparent 1px ${depthGrid}px)`,
            }}
          />
          {gridTicks.map((tick, index) => (
            <div
              key={index}
              style={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: `${tick.t * 100}%`,
                borderLeft: `1px solid ${tick.major ? "var(--grid-line-strong)" : "var(--grid-line)"}`,
                pointerEvents: "none",
              }}
            />
          ))}
        </>
      )}

      {track.lithology && lithologyBlocks && (
        <div style={{ position: "absolute", inset: 0 }}>
          {lithologyBlocks.map((block, index) => {
            const y = ((block.top - top) / span) * 100;
            const h = ((block.base - block.top) / span) * 100;
            if (y > 100 || y + h < 0) return null;
            return (
              <div
                key={index}
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  top: `${y}%`,
                  height: `${h}%`,
                  background: block.fill,
                  borderBottom: "1px solid rgba(0,0,0,.35)",
                }}
              />
            );
          })}
        </div>
      )}

      {/* Litologi yang dideskripsi: warna dari deskripsinya sendiri, dengan
          namanya tercetak pada pita yang cukup tinggi memuatnya. Pita setinggi
          empat piksel yang diberi teks sembilan piksel tidak terbaca dan
          justru menutupi tetangganya — dan sebuah sumur dengan tujuh ratus
          selang cutting hampir seluruhnya berpita setipis itu. */}
      {(track.classes || track.bands) && classBlocks?.length === 0 && (
        <div style={{
          position: "absolute", inset: 0, display: "flex",
          alignItems: "center", justifyContent: "center", padding: 4,
        }}>
          <span style={{
            font: "400 8.5px/1.4 var(--font-sans)", color: "var(--ink-6)",
            textAlign: "center",
          }}>
            {emptyNote ?? "No LRLC curve on this well"}
          </span>
        </div>
      )}

      {(track.classes || track.bands) && classBlocks && (
        <div style={{ position: "absolute", inset: 0 }}>
          {classBlocks.map((block, index) => {
            const y = ((block.top - top) / span) * 100;
            const h = ((block.base - block.top) / span) * 100;
            if (y > 100 || y + h < 0) return null;
            return (
              <div
                key={index}
                title={`${block.label} — ${block.top.toFixed(1)}–${block.base.toFixed(1)} m`}
                style={{
                  position: "absolute", left: 0, right: 0,
                  top: `${y}%`, height: `${h}%`,
                  background: block.fill,
                  // Tanpa garis pemisah, dua pita bertetangga yang warnanya
                  // berdekatan — merah muda dan ungu — terbaca sebagai satu
                  // lapisan, dan justru perbatasan keduanyalah yang dicari.
                  borderBottom: "1px solid rgba(0,0,0,.28)",
                }}
              />
            );
          })}
        </div>
      )}

      {track.described && describedBlocks?.length === 0 && (
        // Track yang meminta deskripsi pada sumur yang tidak punya satu pun
        // adalah kolom kosong yang terbaca seperti kerusakan. Yang tertulis di
        // sini membedakan "tidak ada yang dideskripsi" dari "gagal digambar".
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 4px",
            textAlign: "center",
            font: "400 9px/1.3 var(--font-sans)",
            color: "var(--ink-7)",
            pointerEvents: "none",
          }}
        >
          No described interval on this well
        </div>
      )}

      {track.described && describedBlocks && (
        <div style={{ position: "absolute", inset: 0 }}>
          {describedBlocks.map((block, index) => {
            const y = ((block.top - top) / span) * 100;
            const h = ((block.base - block.top) / span) * 100;
            if (y > 100 || y + h < 0) return null;
            return (
              <div
                key={index}
                title={block.hint}
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  top: `${y}%`,
                  height: `${h}%`,
                  background: block.fill,
                  borderBottom: "1px solid rgba(0,0,0,.28)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                {h > 2.2 && (
                  <span style={{
                    font: "500 8.5px var(--font-mono)",
                    color: "var(--ink)",
                    whiteSpace: "nowrap",
                    // Nama yang lebih panjang dari tracknya dipenggal di sini,
                    // pada lebar yang sebenarnya, dan diberi "…" supaya yang
                    // terbaca mengaku dirinya terpenggal. Tanpa `minWidth: 0`
                    // sebuah anak flex menolak menyempit di bawah lebar
                    // teksnya, dan yang panjang justru meluber ke tetangganya.
                    maxWidth: "100%",
                    minWidth: 0,
                    padding: "0 3px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    pointerEvents: "none",
                  }}>
                    {block.label}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {zones.map((zone) => {
        const y = ((zone.top - top) / span) * 100;
        const h = ((zone.base - zone.top) / span) * 100;
        if (y > 100 || y + h < 0) return null;
        return (
          <div
            key={zone.name}
            title={`${zone.name} · ${zone.top.toFixed(1)}–${zone.base.toFixed(1)} m`}
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              top: `${y}%`,
              height: `${h}%`,
              background: "var(--accent-zone)",
              borderTop: "1px solid var(--accent-zone-line)",
              borderBottom: "1px solid var(--accent-zone-line)",
              pointerEvents: "none",
            }}
          />
        );
      })}

      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="none"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
        aria-label={`Track ${track.title}`}
      >
        {spectrumBars?.map((bar, index) => (
          <rect
            key={index}
            x={0}
            y={bar.y}
            // Pita selebar penuh adalah sebuah log citra; lebar yang ikut nilai
            // melaporkan besaran yang sama dua kali, dan itulah yang membuat
            // sebuah lapisan terbaca dari jarak satu meter.
            width={track.spectrum?.bars ? bar.w : VIEW_W}
            height={bar.h}
            fill={bar.fill}
          />
        ))}
        {/* Arsiran selalu tembus pandang. Sebuah luasan pekat menutupi kurva
            yang menjadi tepinya sendiri, dan menutupi kurva tetangga di track
            yang sama — yang justru menjadi alasan orang menaruh dua kurva
            dalam satu track. */}
        {/* Perhentian gradien dihitung di sini dan bukan sekali di modul, sebab
            skala tampilan tiap kurva berbeda dan sumbu logaritmik membuat
            warna tidak sebanding dengan lebar. Enam belas perhentian sudah
            mulus pada track selebar tiga sentimeter — mata tidak bisa
            membedakannya dari gradien menerus, dan seribu perhentian hanya
            memperbesar DOM. */}
        <defs>
          {fills
            .filter((fill) => fill.ramp)
            .map((fill) => {
              const stops = rampStops(fill.ramp!);
              // Menurun sepanjang lubang, bukan melintang: warnanya mengikuti
              // bacaan pada tiap kedalaman, dan seluruh lebar arsiran di
              // kedalaman itu memakai satu warna. `userSpaceOnUse` dipakai
              // karena kotak batas path arsiran hanya setinggi bagian yang
              // benar-benar terisi — perhentian yang dihitung terhadap jendela
              // kedalaman akan bergeser kalau diukur terhadap kotak itu.
              if (fill.by === "value") {
                return (
                  <linearGradient key={fill.index} id={gradientId(fill.index, fill.name)}
                                  gradientUnits="userSpaceOnUse"
                                  x1="0" y1="0" x2="0" y2={VIEW_H}>
                    {fill.stops.map((stop, i) => (
                      <stop key={i} offset={`${stop.offset.toFixed(3)}%`}
                            stopColor={rampColor(stops, fill.reverse ? 1 - stop.t : stop.t)} />
                    ))}
                  </linearGradient>
                );
              }
              return (
                <linearGradient key={fill.index} id={gradientId(fill.index, fill.name)}
                                x1="0" y1="0" x2="1" y2="0">
                  {Array.from({ length: 16 }, (_, i) => {
                    const t = i / 15;
                    const value = unproject(t * VIEW_W, fill.min, fill.max, track.logarithmic);
                    // Warna mengikuti nilai, bukan lebar: pada sumbu terbalik
                    // ujung kiri adalah nilai terbesar, dan rampnya harus ikut
                    // terbalik tanpa ada yang menyuruhnya.
                    const f = rampFraction(value, fill.min, fill.max, track.logarithmic);
                    return (
                      <stop key={i} offset={`${(t * 100).toFixed(2)}%`}
                            stopColor={rampColor(stops, fill.reverse ? 1 - f : f)} />
                    );
                  })}
                </linearGradient>
              );
            })}
        </defs>
        {opposites.map((fill) => (
          <path
            key={`seberang-${fill.index}`}
            d={fill.d}
            fill={fill.color}
            fillOpacity={FILL_OPACITY}
            stroke="none"
            aria-label={`Opposite shading of ${fill.name}`}
          />
        ))}
        {fills.map((fill) => (
          <path
            key={fill.index}
            d={fill.d}
            fill={fill.ramp ? `url(#${gradientId(fill.index, fill.name)})` : fill.color}
            fillOpacity={fill.ramp && fill.by === "value" ? VALUE_FILL_OPACITY : FILL_OPACITY}
            stroke="none"
            aria-label={`Shading of ${fill.name}`}
          />
        ))}
        {shading?.d && (
          <path
            d={shading.d}
            fill={shading.color}
            fillOpacity={FILL_OPACITY}
            stroke="none"
            aria-label={`Shading of ${track.shading?.curve} over ${track.shading?.against}`}
          />
        )}
        {paths.map(({ curve, index, d }) => {
          if (!d) return null;
          const width = curve.width ?? weight;
          return (
            <path
              key={index}
              d={d}
              fill="none"
              stroke={curve.color}
              strokeWidth={width}
              strokeDasharray={dashArrayFor(curveStyleOf(curve), width)}
              // Titik yang digambar dengan ujung persegi menjadi garis putus
              // pendek, bukan titik; ujung bundar yang membedakan keduanya.
              strokeLinecap={curveStyleOf(curve) === "dotted" ? "round" : "butt"}
              vectorEffect="non-scaling-stroke"
            />
          );
        })}
      </svg>

      {/* Garis ambang: tegak, pada nilai yang disebutnya, dengan skala kurva
          yang dibandingkan dengannya. Diletakkan sebelum marker supaya garis
          kedalaman melintas di atasnya, bukan sebaliknya. */}
      {(track.references ?? []).map((reference) => {
        const curve = track.curves.find((c) => c.name === reference.curve);
        if (!curve) return null;
        const x = project(reference.value, curve.min, curve.max,
                          curve.logarithmic ?? track.logarithmic);
        if (!Number.isFinite(x) || x < 0 || x > VIEW_W) return null;
        return (
          <div
            key={`${reference.curve}:${reference.value}`}
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: `${(x / VIEW_W) * 100}%`,
              borderLeft: `1.2px dashed ${reference.color}`,
              pointerEvents: "none",
            }}
            title={`${reference.curve} cut-off ${formatReading(reference.value)}`}
          >
            {reference.label && (
              <span
                style={{
                  // Menempel di tepi atas jendela yang sedang tergulir, bukan
                  // di tepi atas sumur. Sebuah lembar sepanjang dua ratus meter
                  // pada skala 1:200 lebih tinggi daripada layarnya, dan tulisan
                  // yang duduk di kedalaman teratas terbaca sekali lalu hilang —
                  // meninggalkan garis tegak tanpa keterangan, yang persis
                  // seperti garis yang tidak ada artinya.
                  position: "sticky",
                  top: 2,
                  display: "inline-block",
                  marginLeft: 3,
                  font: "500 8.5px var(--font-mono)",
                  color: reference.color,
                  background: "var(--canvas-deep)",
                  padding: "0 2px",
                  borderRadius: 2,
                  whiteSpace: "nowrap",
                }}
              >
                {reference.label}
              </span>
            )}
          </div>
        );
      })}

      {markers.map((marker) => {
        const y = ((marker.md - top) / span) * 100;
        if (y < 0 || y > 100) return null;
        return (
          <div
            key={marker.name}
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              top: `${y}%`,
              borderTop: `1.4px dashed ${marker.color}`,
              pointerEvents: "none",
            }}
          />
        );
      })}

      {/* Picker: garis bidik, satu titik pada setiap kurva di kedalaman itu,
          dan angkanya. Titiknya penting sendiri — angka tanpa titik memaksa
          pembacanya menebak kurva mana yang sedang dilaporkan ketika dua kurva
          berpotongan, dan di situlah pembacaan justru paling sering diperlukan. */}
      {cursorAt !== null && (
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          <div
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              top: `${cursorAt}%`,
              borderTop: "1px solid var(--accent)",
            }}
          />
          {readings.map(({ curve, index, t }) =>
            t === null ? null : (
              <span
                key={index}
                style={{
                  position: "absolute",
                  left: `${t * 100}%`,
                  top: `${cursorAt}%`,
                  width: 7,
                  height: 7,
                  marginLeft: -3.5,
                  marginTop: -3.5,
                  borderRadius: "50%",
                  background: curve.color,
                  border: "1px solid var(--canvas-deep)",
                }}
              />
            ),
          )}
          {showReadings && readings.length > 0 && (
            <div
              style={{
                position: "absolute",
                left: 3,
                right: 3,
                top: `${cursorAt}%`,
                // Di paruh bawah track, angkanya naik ke atas garis bidik agar
                // tidak terpotong tepi bawah panel.
                transform: cursorAt > 55 ? "translateY(calc(-100% - 4px))" : "translateY(4px)",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 1,
              }}
            >
              {readings.map(({ curve, index, value }) => (
                <span
                  key={index}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    maxWidth: "100%",
                    padding: "0 4px",
                    borderRadius: 3,
                    background: "var(--panel)",
                    border: "1px solid var(--line-strong)",
                    font: "500 9.5px var(--font-mono)",
                    color: "var(--ink-2)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                  }}
                >
                  <span
                    style={{ width: 5, height: 5, borderRadius: "50%", background: curve.color, flex: "none" }}
                  />
                  {curve.name} {formatReading(value)}
                  {curve.unit ? <span style={{ color: "var(--ink-6)" }}>{curve.unit}</span> : null}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Kotak zoom yang sedang diseret di atas lembar.
 *
 * Ia mencetak kedua kedalaman batasnya, bukan hanya sebuah kotak berwarna.
 * Sebuah kotak polos memberi tahu bahwa sesuatu sedang dipilih; angkanya
 * memberi tahu apa — dan seret zoom hampir selalu dimulai dari sebuah batas
 * lapisan yang sudah punya kedalaman di kepala orang yang menyeretnya.
 */
export function BoxZoomOverlay({
  from,
  to,
  height,
  top,
  base,
  format,
}: {
  /** Kedua tepi kotak dalam piksel isi lembar. */
  from: number;
  to: number;
  /** Tinggi isi lembar, penerjemah piksel menjadi kedalaman. */
  height: number;
  top: number;
  base: number;
  format: (depth: number) => string;
}) {
  const span = base - top;
  const depthAt = (y: number) => (height > 0 && span !== 0 ? top + (y / height) * span : top);
  const lo = depthAt(from);
  const hi = depthAt(to);
  return (
    <div
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        top: from,
        height: Math.max(to - from, 1),
        background: "var(--accent-zone)",
        borderTop: "1px solid var(--accent)",
        borderBottom: "1px solid var(--accent)",
        pointerEvents: "none",
        zIndex: 4,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <span
        style={{
          padding: "1px 5px",
          borderRadius: 3,
          background: "var(--accent)",
          color: "var(--accent-contrast)",
          font: "600 9.5px var(--font-mono)",
          whiteSpace: "nowrap",
        }}
      >
        {format(lo)} – {format(hi)}
      </span>
    </div>
  );
}

/** Turn an API curve into a drawable track curve using its stored display scale. */
export function toTrackCurve(
  curve: CurveData,
  overrides: Partial<TrackCurve> = {},
): TrackCurve {
  const finite = curve.values.filter((v): v is number => v !== null && Number.isFinite(v));
  const fallbackMin = finite.length ? Math.min(...finite) : 0;
  const fallbackMax = finite.length ? Math.max(...finite) : 1;
  const merged: TrackCurve = {
    name: curve.mnemonic,
    color: curve.color || "var(--ink-3)",
    min: curve.display_min ?? fallbackMin,
    max: curve.display_max ?? fallbackMax,
    unit: curve.unit,
    depths: curve.depths,
    values: curve.values,
    ...overrides,
  };
  // Skala dibereskan di satu tempat ini, sesudah setelan pengguna diterapkan:
  // rentang yang menyentuh nol pada sumbu logaritmik membuat setiap titiknya
  // menjadi NaN, dan track yang kosong tanpa sebab adalah kegagalan yang tidak
  // terbaca oleh siapa pun yang memandangnya.
  const [min, max] = resolveAxisRange(merged.min, merged.max, merged.logarithmic ? "log" : "linear");
  return { ...merged, min, max };
}

/**
 * Reading at a depth, for the cursor readout — nearest sample, not interpolated,
 * so the number shown is one that actually exists in the data.
 *
 * Pencarian dilakukan secara biner alih-alih dengan menganggap langkah
 * kedalamannya seragam. Kurva yang sampai ke layar sudah melewati penjarangan
 * di server, dan sebuah kurva yang langkahnya berubah di tengah — sambungan
 * dua lari alat, atau bagian yang tidak terekam — membuat perkiraan indeks dari
 * dua sampel pertama meleset makin jauh seiring bertambahnya kedalaman.
 *
 * `window` adalah berapa sampel di kiri dan kanan yang masih boleh dipungut
 * ketika sampel terdekat kosong. Nol berarti jawaban tepat pada sampel itu;
 * bawaannya dua, supaya angka picker tidak berkedip hilang setiap kali kursor
 * melintasi satu sampel null di tengah kurva yang utuh.
 */
export function sampleAt(curve: TrackCurve, depth: number, window = 2): number | null {
  const depths = curve.depths;
  if (!depths.length) return null;
  // Sumbu ketinggian menurun sepanjang lubang, jadi larik kedalamannya menurun
  // pula. Pencarian biner tetap satu pencarian biner — yang dibalik hanyalah
  // perbandingannya — tetapi mengabaikannya berarti selalu mendarat di ujung
  // larik, dan picker membacakan sampel dari kedalaman yang lain sama sekali.
  const descending = depths.length > 1 && depths[depths.length - 1] < depths[0];
  const before = (value: number) => (descending ? value > depth : value < depth);
  let lo = 0;
  let hi = depths.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (before(depths[mid])) lo = mid + 1;
    else hi = mid;
  }
  let index = lo;
  if (index > 0 && Math.abs(depths[index - 1] - depth) <= Math.abs(depths[index] - depth)) index -= 1;
  // Di luar rentang kurva tidak ada bacaan sama sekali — memungut sampel ujung
  // untuk kedalaman yang tak pernah diukur berarti melaporkan angka karangan.
  const step = depths.length > 1 ? Math.abs(depths[1] - depths[0]) : 0;
  const first = depths[0];
  const last = depths[depths.length - 1];
  const near = Math.min(first, last) - step;
  const far = Math.max(first, last) + step;
  if (depth < near || depth > far) return null;
  for (let offset = 0; offset <= window; offset++) {
    for (const at of offset === 0 ? [index] : [index - offset, index + offset]) {
      if (at < 0 || at >= curve.values.length) continue;
      const value = curve.values[at];
      if (value !== null && Number.isFinite(value)) return value;
    }
  }
  return null;
}

/**
 * Variable colour fill — the permeability track style of a correlation sheet.
 *
 * Each bar is coloured by its own value rather than shaded in one colour, and
 * its width follows that value too. Two cues for one quantity is redundant in
 * information terms, but it is what makes a bed readable from a metre away, and
 * that is what a correlation sheet is for.
 *
 * Samples are binned into `bins` depth boxes first. A 20,000-sample log drawn
 * one sample at a time gives 20,000 SVG elements per track per well; binned to
 * 400, it looks no different on screen and the element count
 * elemennya turun lima puluh kali.
 */
export function buildSpectrumBars(
  curve: TrackCurve,
  top: number,
  base: number,
  bins = 400,
  logarithmic?: boolean,
): { y: number; h: number; w: number; t: number }[] {
  if (!axisSpan(top, base)) return [];
  const sum = new Float64Array(bins);
  const count = new Int32Array(bins);
  for (let i = 0; i < curve.depths.length; i++) {
    const t = axisFraction(curve.depths[i], top, base);
    const value = curve.values[i];
    if (t === null || value === null || !Number.isFinite(value)) continue;
    const bin = Math.min(bins - 1, Math.floor(t * bins));
    sum[bin] += value;
    count[bin] += 1;
  }
  const height = VIEW_H / bins;
  const out: { y: number; h: number; w: number; t: number }[] = [];
  for (let b = 0; b < bins; b++) {
    if (!count[b]) continue;
    const mean = sum[b] / count[b];
    const x = project(mean, curve.min, curve.max, curve.logarithmic ?? logarithmic);
    if (!Number.isFinite(x)) continue;
    out.push({
      y: b * height,
      h: height + 0.6,
      w: Math.max(0, Math.min(VIEW_W, x)),
      // Position on the display scale, used to pick the colour.
      t: Math.max(0, Math.min(1, x / VIEW_W)),
    });
  }
  return out;
}
